import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DataTableDirective, DataTablesModule } from 'angular-datatables';
import { Config } from 'datatables.net';
import { Subject } from 'rxjs';
import { Article } from '../../../models/article';
import { ArticleInvoice } from '../../../models/article-invoice';
import { InvoiceStatus } from '../../../models/invoice-status';
import { Partner } from '../../../models/partner';
import { ArticleService } from '../../../services/article.service';
import { AuthenticationService } from '../../../services/auth/authentication.service';
import { InvoiceService } from '../../../services/invoice.service';
import { ModalService } from '../../../services/modal/modal.service';
import { PartnerService } from '../../../services/partner.service';

@Component({
  selector: 'app-invoice-form',
  imports: [DataTablesModule, ReactiveFormsModule, CommonModule],
  templateUrl: './invoice-form.component.html',
  styleUrl: './invoice-form.component.scss',
})
export class InvoiceFormComponent {
  @Input()
  form!: FormGroup;

  @Input()
  buttonText!: string;

  @Input()
  errorProvider!: Subject<string | undefined>;

  error?: string;
  partnerPlaceholder?: any;
  statusPlaceholder?: any;

  @Input()
  submittingProvider!: Subject<boolean>;

  submitting: boolean = false;
  fullyLoaded: boolean = false;

  @Output()
  clicked: EventEmitter<void> = new EventEmitter();

  articles!: Article[];
  articlesCached!: Article[];
  articleInvoices: ArticleInvoice[] = [];

  statuses!: InvoiceStatus[];

  partners!: Partner[];

  dtOptions!: Config;

  @ViewChild(DataTableDirective)
  datatableElement!: DataTableDirective;
  dtTrigger: Subject<any> = new Subject<any>();

  constructor(
    private partnerService: PartnerService,
    private articleService: ArticleService,
    private invoiceService: InvoiceService,
    private authService: AuthenticationService,
    private fb: FormBuilder,
    private modalService: ModalService,
    private vcr: ViewContainerRef,
    private destroyRef: DestroyRef,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form
      .get('date')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (date: string) => {
          this.invoiceService
            .getCurrentInvoiceNumber(Number(date.split('-')[0]))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (resp) =>
                this.form.get('invoiceNumber')?.setValue(resp.invoiceNumber),
              error: (err: HttpErrorResponse) => {
                if (err.status == 401) {
                  this.authService.logout();
                } else {
                  this.submitting = false;
                  this.modalService.showErrorDialog(this.vcr, err.error.message);
                }
              },
            });
        },
      });

    this.errorProvider
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((error) => (this.error = error));
    this.submittingProvider
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((submitting) => (this.submitting = submitting));

    this.articleService
      .getArticles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.articleInvoices = this.form.get('articles')?.value;
          this.articles = resp.filter(
            (a) =>
              !this.articleInvoices.map((ai) => ai.articleId).includes(a.id)
          );
          this.articlesCached = resp;
          this.updateLoading();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 401) {
            this.authService.logout();
          } else {
            this.submitting = false;
            this.modalService.showErrorDialog(this.vcr, err.error.message);
          }
        },
      });

    this.partnerService
      .getPartners()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.partners = resp;
          this.updateLoading();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 401) {
            this.authService.logout();
          } else {
            this.submitting = false;
            this.modalService.showErrorDialog(this.vcr, err.error.message);
          }
        },
      });

    this.invoiceService
      .getStatuses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.statuses = resp;
          this.updateLoading();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 401) {
            this.authService.logout();
          } else {
            this.submitting = false;
            this.modalService.showErrorDialog(this.vcr, err.error.message);
          }
        },
      });

    this.dtOptions = {
      paging: false,
      searching: false,
      destroy: true,
      info: false,
      ajax: (dataTablesParameters: any, callback) => {
        callback({ data: this.articleInvoices });
      },
      lengthMenu: [5, 10, 20, 50],
      columns: [
        {
          title: 'Article',
          data: 'name',
        },
        {
          title: 'Unit of measurement',
          data: 'unitOfMeasurement',
        },
        {
          title: 'Price per unit',
          data: 'price',
          render(data, type, row, meta) {
            return `<input id="price${row.articleId}" value="${data}" class="form-control"/>`;
          },
        },
        {
          title: 'Quantity',
          data: 'quantity',
          render(data, type, row, meta) {
            return `<input id="quantity${row.articleId}" value="${data}" class="form-control"/>`;
          },
        },
        {
          title: 'Action',
          data: null,
          orderable: false,
          render: (data, type, row, meta) => {
            return `<a href="javascript: void(0);" id="delete" class="action-icon"> <i class="mdi mdi-delete"></i></a>`;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | object, index: number) => {
        let deleteIcon: any = $('td:last-child #delete', row);
        let quantityInput: any = $(
          'td input#quantity' + (data as ArticleInvoice).articleId,
          row
        );
        let priceInput = $(
          'td input#price' + (data as ArticleInvoice).articleId,
          row
        );

        deleteIcon.off('click');
        deleteIcon.on('click', () =>
          this.removeArticle((data as ArticleInvoice).articleId)
        );
        quantityInput.off('input');
        quantityInput.on('input', () => {
          this.changeArticleQuantity(
            data as ArticleInvoice,
            quantityInput.val() as number
          );
        });
        priceInput.off('input');
        priceInput.on('input', () => {
          this.changeArticlePrice(
            data as ArticleInvoice,
            priceInput.val() as number
          );
        });
      },
    };
  }

  ngAfterViewInit(): void {
    this.changeDetector.detectChanges();
    this.dtTrigger.next(null);
  }

  addArticle() {
    if (!this.articles) {
      return;
    }

    let article: Article = this.articles.filter(
      (a) => a.id === this.form.controls['article'].value
    )[0];

    if (article) {
      this.articles = this.articles.filter((a) => a.id !== article.id);
      this.form.controls['article'].reset();

      let articleInvoice: ArticleInvoice = {
        articleId: article.id,
        name: article.name,
        unitOfMeasurement: article.unitOfMeasurement,
        quantity: 1,
        price: article.price,
      };

      this.articleInvoices.push(articleInvoice);
      this.dtTrigger.next(null);
      this.calculateAmount();
    }
  }

  removeArticle(articleId: number) {
    if (!this.articlesCached) {
      return;
    }

    this.articleInvoices = this.articleInvoices.filter(
      (a: ArticleInvoice) => a.articleId !== articleId
    );

    let removedArticle: Article = this.articlesCached.filter(
      (a) => a.id === articleId
    )[0];

    this.articles.push(removedArticle);

    this.articles = this.articles.sort((a, b) => a.id - b.id);

    this.changeDetector.detectChanges();
    this.dtTrigger.next(null);
    this.calculateAmount();
  }

  changeArticleQuantity(articleInvoice: ArticleInvoice, quantity: number) {
    this.articleInvoices.forEach((a) => {
      if (a.articleId == articleInvoice.articleId) {
        a.quantity = quantity;
      }
    });
    this.calculateAmount();
  }

  changeArticlePrice(articleInvoice: ArticleInvoice, price: number) {
    this.articleInvoices.forEach((a) => {
      if (a.articleId == articleInvoice.articleId) {
        a.price = price;
      }
    });
    this.calculateAmount();
  }

  private calculateAmount() {
    this.form.controls['amount'].setValue(0);
    this.articleInvoices.forEach((a) => {
      this.form.controls['amount'].setValue(
        this.form.controls['amount'].value + a.price * a.quantity
      );
    });
  }

  command() {
    let articles: FormArray = this.form.get('articles') as FormArray;
    articles.clear();

    this.articleInvoices.forEach((a) => {
      articles.push(
        this.fb.group({
          articleId: this.fb.control(a.articleId, [
            Validators.required,
            Validators.min(1),
          ]),
          quantity: this.fb.control(a.quantity, [
            Validators.required,
            Validators.min(1),
          ]),
          price: this.fb.control(a.price, [
            Validators.required,
            Validators.min(0.0),
          ]),
        })
      );
    });

    this.clicked.emit();
  }

  updateLoading() {
    this.fullyLoaded =
      this.partners != undefined &&
      this.statuses != undefined &&
      this.articles != undefined;
    if (this.fullyLoaded) {
      this.changeDetector.detectChanges();
      this.dtTrigger.next(null);
    }
  }
}
